import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMButton, PMDataTable, PMEmptyState, PMFilterBar, PMSegmented, PMSkeleton, PMTabs } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { usePeople, useVisits, type Visit } from "@/data/queries";
import { levelDef } from "@/lib/levels";
import { Page, Eyebrow } from "@/ui/Page";
import { VisitStatusBadge } from "@/ui/visit";
import { FieldVisitCard } from "@/ui/FieldVisitCard";
import { Character } from "@/ui/Character";
import { STATUS_META, visitStatus, type VisitStatus } from "@/ui/visitStatus";
import { clock, dayKey, fullName, relTime, shortDate } from "@/ui/format";

const TABS: { id: string; label: string; match: (s: VisitStatus) => boolean }[] = [
  { id: "all", label: "All", match: () => true },
  { id: "review", label: "Needs review", match: (s) => s === "review" || s === "failed" },
  { id: "active", label: "In the field", match: (s) => s === "in_progress" || s === "processing" },
  { id: "signed", label: "Signed", match: (s) => s === "signed" },
];

function dayLabel(key: string) {
  const today = dayKey(new Date().toISOString());
  const yest = dayKey(new Date(Date.now() - 86_400_000).toISOString());
  if (key === today) return "Today";
  if (key === yest) return "Yesterday";
  return new Date(key + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export function VisitsPage() {
  const { teamId } = useParams({ from: "/t/$teamId/visits" });
  const search = useSearch({ from: "/t/$teamId/visits" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const visits = useVisits();
  const people = usePeople();
  const [view, setView] = useState<"cards" | "table">("cards");
  const tab = search.status || "all";
  const q = (search.q || "").toLowerCase();
  const setSearch = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);

  const counts = useMemo(() => Object.fromEntries(TABS.map((t) => [t.id, visits.data.filter((v) => t.match(visitStatus(v))).length])), [visits.data]);
  const rows = useMemo(() => visits.data.filter((v) => {
    if (!TABS.find((t) => t.id === tab)!.match(visitStatus(v))) return false;
    if (search.who && v.user_id !== search.who) return false;
    if (q && !`${v.title} ${v.location_label} ${v.ai_summary}`.toLowerCase().includes(q)) return false;
    return true;
  }), [visits.data, tab, search.who, q]);

  const groups = useMemo(() => {
    const g = new Map<string, Visit[]>();
    for (const v of rows) {
      const k = dayKey(v.started_at || v.created_at);
      g.set(k, [...(g.get(k) || []), v]);
    }
    return [...g.entries()];
  }, [rows]);

  const open = (v: Visit) => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: v.id } });
  const who = people.data.find((p) => p.user_id === search.who);

  return (
    <Page
      chip="FIELD WORK"
      title="Field Visits"
      subtitle={`${visits.data.length} visits · ${counts.review} need review`}
      actions={<PMSegmented value={view} onChange={(v: string) => setView(v as any)} options={[{ value: "cards", label: "Cards" }, { value: "table", label: "Table" }]} />}
    >
      <PMTabs value={tab} onChange={(id: string) => setSearch({ status: id === "all" ? undefined : id })} tabs={TABS.map((t) => ({ id: t.id, label: t.label, count: counts[t.id] }))} aria-label="Visit status" />
      <PMFilterBar
        search={search.q || ""}
        searchPlaceholder={`Search visits, ${hierarchy.levels.at(-1)!.plural.toLowerCase()}, summaries`}
        onSearch={(v: string) => setSearch({ q: v || undefined })}
        filters={[{ key: "who", label: "Advisor", icon: "user-outline", searchable: true, value: search.who, options: people.data.map((p) => ({ value: p.user_id, label: fullName(p) })), onChange: (v: string | string[]) => setSearch({ who: (v as string) || undefined }) }]}
        activeChips={who ? [{ key: "who", label: fullName(who) }] : []}
        onRemoveChip={() => setSearch({ who: undefined })}
        onClearAll={() => setSearch({ who: undefined, q: undefined })}
      />

      {visits.isLoading && !visits.data.length ? (
        <div className="grid grid-3">{[0, 1, 2, 3, 4, 5].map((i) => <PMSkeleton key={i} shape="rect" height={200} radius={16} />)}</div>
      ) : rows.length === 0 ? (
        visits.data.length ? <PMEmptyState title="No visits match" description="Try another filter or clear the search." action={<PMButton variant="text" onClick={() => setSearch({ q: undefined, who: undefined, status: undefined })}>Clear filters</PMButton>} />
          : <Character name="bukkie" size={120} title="No field visits yet" line="Visits start on the PropelMapper phone app and appear here as they sync." />
      ) : view === "table" ? (
        <div className="card">
          <PMDataTable
            rowKey="id"
            onRowClick={open}
            rows={rows}
            columns={[
              { key: "title", label: "Visit", sortable: true, render: (v: Visit) => <strong>{v.title || "Field visit"}</strong> },
              { key: "location_label", label: hierarchy.levels.at(-1)!.label, sortable: true, render: (v: Visit) => <span>{levelDef(v.location_level, hierarchy.key).emoji} {v.location_label}</span> },
              { key: "started_at", label: "Date", sortable: true, render: (v: Visit) => `${shortDate(v.started_at)} · ${clock(v.started_at)}`, sortValue: (v: Visit) => v.started_at },
              { key: "user_id", label: "Advisor", render: (v: Visit) => fullName(people.data.find((p) => p.user_id === v.user_id)) },
              { key: "obs_count", label: "Obs", align: "right", sortable: true },
              { key: "status", label: "Status", render: (v: Visit) => <VisitStatusBadge v={v} />, sortValue: (v: Visit) => STATUS_META[visitStatus(v)].label },
            ]}
          />
        </div>
      ) : (
        groups.map(([k, list]) => (
          <section key={k} className="day-group">
            <Eyebrow count={list.length} action={<span className="hint">{relTime(list[0].started_at)}</span>}>{dayLabel(k)}</Eyebrow>
            <div className="visit-grid">{list.map((v) => <FieldVisitCard key={v.id} v={v} onOpen={() => open(v)} />)}</div>
          </section>
        ))
      )}
    </Page>
  );
}
